import { useEffect, useRef, useState } from 'react';

const OBSERVER_OPTIONS = Object.freeze({
  root: null,
  rootMargin: '0px',
  threshold: 0.1,
});

interface IUsePostObserver {
  isObserved?: boolean;
  onFirstIntersect?: Function;
  onLeaveView?: Function;
  onEnterInView?: Function;
}

/**
 * It only observes the intersection of the post with the BOTTOM of the viewport.
 * @param isObserved it determines whether the observer should be triggered
 * @param onFirstIntersect function only triggered when the post is first shown
 * @param onEnterInView function triggered everytime the post enters the viewport from the bottom
 * @param onLeaveView function triggered everytime the post leaves the viewport from the bottom
 */
export const usePostObserverOnBottom = ({
  isObserved,
  onFirstIntersect,
  onLeaveView,
  onEnterInView,
}: IUsePostObserver) => {
  const ref = useRef<HTMLDivElement | null>(null);

  // In order to get the up-to-date value of hasIntersectedOnce in the callback, we link it to a ref
  const [hasIntersectedOnce, setHasIntersectedOnce] = useState<boolean>(false);
  const hasIntersectedOnceRef = useRef(hasIntersectedOnce);
  useEffect(() => {
    hasIntersectedOnceRef.current = hasIntersectedOnce;
  }, [hasIntersectedOnce]);

  const callback = (entries: any) => {
    const { isIntersecting, boundingClientRect } = entries[0];
    const isBelowViewport = boundingClientRect.y > 0;

    // ONLY INTERESTED IN THE BOTTOM INTERSECTIONS
    if (!isBelowViewport) {
      return;
    }

    if (isIntersecting && !hasIntersectedOnceRef.current) {
      setHasIntersectedOnce(true);
    }
    if (isIntersecting) {
      onEnterInView?.();
    } else if (hasIntersectedOnceRef.current) {
      onLeaveView?.();
    }
  };

  useEffect(() => {
    if (isObserved && hasIntersectedOnce) {
      onFirstIntersect?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIntersectedOnce]);

  useEffect(() => {
    const currentRef = ref?.current;
    const observer = new IntersectionObserver(callback, OBSERVER_OPTIONS);

    if (isObserved && currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (isObserved && currentRef) {
        observer.unobserve(currentRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isObserved, ref]);

  return [ref];
};
